import React, { useState, useEffect, use } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Plus, Edit2, Trash2, Calendar, Percent, Tag, X, Save } from 'lucide-react';
import AlertModal from './AlertModal';
import { useAlert } from '../hooks/UseAlert';

const PromotionManager = () => {
  const [promotions, setPromotions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [loading, setLoading] = useState(false);
  const { success, error, warning, alertConfig, closeAlert } = useAlert();

  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    discountPercent: '',
    applicationType: 'all', // 'all', 'selected', 'excluded'
    selectedCategories: [],
    excludedCategories: [],
    description: ''
  });

  // Catégories de services disponibles
  const serviceCategories = [
    "Soins Visage",
    "Rituels Minéraux Visage",
    "Voyage Autour du Monde",
    "Massages Autour des Femmes",
    "Massages Eveil des sens",
    "Massages Énergétiques",
    "Rituels du Corps",
    "Rituels au coeur des délices",
    "Collection Tour du Monde",
    "Univers Spa-Massage"
  ];

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      const promotionsRef = collection(db, 'promotions');
      const q = query(promotionsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const promoData = [];
      querySnapshot.forEach((doc) => {
        promoData.push({ id: doc.id, ...doc.data() });
      });
      
      setPromotions(promoData);
    } catch (error) {
      console.error('Erreur chargement promotions:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      discountPercent: '',
      applicationType: 'all',
      selectedCategories: [],
      excludedCategories: [],
      description: ''
    });
    setEditingPromotion(null);
    setShowForm(false);
  };

  const handleEdit = (promotion) => {
    setFormData({
      name: promotion.name,
      startDate: promotion.startDate,
      endDate: promotion.endDate,
      discountPercent: promotion.discountPercent,
      applicationType: promotion.applicationType,
      selectedCategories: promotion.selectedCategories || [],
      excludedCategories: promotion.excludedCategories || [],
      description: promotion.description || ''
    });
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.startDate || !formData.endDate || !formData.discountPercent) {
      warning('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.discountPercent < 1 || formData.discountPercent > 100) {
      error('Le pourcentage doit être entre 1 et 100');
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      error('La date de début doit être avant la date de fin');
      return;
    }

    setLoading(true);

    try {
      const promotionData = {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        discountPercent: parseInt(formData.discountPercent),
        applicationType: formData.applicationType,
        selectedCategories: formData.selectedCategories,
        excludedCategories: formData.excludedCategories,
        description: formData.description,
        isActive: true,
        updatedAt: new Date().toISOString()
      };

      if (editingPromotion) {
        // Mise à jour
        await updateDoc(doc(db, 'promotions', editingPromotion.id), promotionData);
      } else {
        // Création
        promotionData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'promotions'), promotionData);
      }

      loadPromotions();
      resetForm();
    } catch (error) {
      console.error('Erreur sauvegarde promotion:', error);
      error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (promotionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'promotions', promotionId));
      success('Promotion supprimée');
      loadPromotions();
    } catch (error) {
      console.error('Erreur suppression:', error);
      error('Erreur lors de la suppression');
    }
  };

  const toggleCategory = (category, type) => {
    const field = type === 'selected' ? 'selectedCategories' : 'excludedCategories';
    const categories = formData[field];
    
    if (categories.includes(category)) {
      setFormData({
        ...formData,
        [field]: categories.filter(c => c !== category)
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...categories, category]
      });
    }
  };

  const getPromotionStatus = (promotion) => {
    const now = new Date();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);

    if (now < start) {
      return { status: 'upcoming', label: 'À venir', color: 'bg-blue-100 text-blue-800' };
    } else if (now > end) {
      return { status: 'expired', label: 'Expirée', color: 'bg-gray-100 text-gray-800' };
    } else {
      return { status: 'active', label: 'Active', color: 'bg-green-100 text-green-800' };
    }
  };

  const getApplicableCategories = (promotion) => {
    if (promotion.applicationType === 'all') {
      return 'Tous les services';
    } else if (promotion.applicationType === 'selected') {
      return promotion.selectedCategories.join(', ');
    } else if (promotion.applicationType === 'excluded') {
      const excluded = promotion.excludedCategories;
      return `Tous sauf : ${excluded.join(', ')}`;
    }
    return '';
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />

    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-purple-900">Gestion des Promotions</h2>
          <p className="text-gray-600 mt-1">Créez et gérez vos promotions saisonnières</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouvelle promotion
        </button>
      </div>

      {/* Formulaire de création/modification */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-light text-purple-900">
                {editingPromotion ? 'Modifier la promotion' : 'Nouvelle promotion'}
              </h3>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nom de la promotion */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4 inline mr-2" />
                  Nom de la promotion *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Soldes d'hiver, Fête des mères..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Pourcentage de réduction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Percent className="w-4 h-4 inline mr-2" />
                  Pourcentage de réduction * (1-100)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
                  placeholder="15"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              {/* Type d'application */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Application de la promotion *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                    <input
                      type="radio"
                      name="applicationType"
                      value="all"
                      checked={formData.applicationType === 'all'}
                      onChange={(e) => setFormData({ ...formData, applicationType: e.target.value })}
                      className="text-purple-600"
                    />
                    <span>Tous les services</span>
                  </label>

                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                    <input
                      type="radio"
                      name="applicationType"
                      value="selected"
                      checked={formData.applicationType === 'selected'}
                      onChange={(e) => setFormData({ ...formData, applicationType: e.target.value })}
                      className="text-purple-600"
                    />
                    <span>Catégories sélectionnées uniquement</span>
                  </label>

                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                    <input
                      type="radio"
                      name="applicationType"
                      value="excluded"
                      checked={formData.applicationType === 'excluded'}
                      onChange={(e) => setFormData({ ...formData, applicationType: e.target.value })}
                      className="text-purple-600"
                    />
                    <span>Tous les services sauf certains</span>
                  </label>
                </div>
              </div>

              {/* Sélection des catégories */}
              {formData.applicationType === 'selected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionnez les catégories concernées
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                    {serviceCategories.map((category) => (
                      <label key={category} className="flex items-center gap-2 p-2 hover:bg-purple-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.selectedCategories.includes(category)}
                          onChange={() => toggleCategory(category, 'selected')}
                          className="text-purple-600"
                        />
                        <span className="text-sm">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.applicationType === 'excluded' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionnez les catégories à exclure
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                    {serviceCategories.map((category) => (
                      <label key={category} className="flex items-center gap-2 p-2 hover:bg-red-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.excludedCategories.includes(category)}
                          onChange={() => toggleCategory(category, 'excluded')}
                          className="text-red-600"
                        />
                        <span className="text-sm">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Description optionnelle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la promotion..."
                  rows="3"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none resize-none"
                />
              </div>

              {/* Boutons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-5 h-5" />
                  {loading ? 'Sauvegarde...' : editingPromotion ? 'Mettre à jour' : 'Créer la promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des promotions */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {promotions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Percent className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucune promotion créée pour le moment</p>
            <p className="text-sm mt-2">Cliquez sur "Nouvelle promotion" pour commencer</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Promotion</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Période</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Réduction</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Catégories</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-purple-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((promo) => {
                  const status = getPromotionStatus(promo);
                  return (
                    <tr key={promo.id} className="border-b border-gray-100 hover:bg-purple-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-gray-900">{promo.name}</p>
                          {promo.description && (
                            <p className="text-xs text-gray-500 mt-1">{promo.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <div>
                          <p>Du {new Date(promo.startDate).toLocaleDateString('fr-FR')}</p>
                          <p>au {new Date(promo.endDate).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-lg font-bold text-purple-700">-{promo.discountPercent}%</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700 max-w-xs">
                        <p className="truncate">{getApplicableCategories(promo)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(promo)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(promo.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default PromotionManager;